import {
  Box,
  Button,
  Chip,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Container, DataGrid } from '@/components/common';
import { useCourseList } from '@/hooks/courses.hook';
import { formatDate } from '@/util/dateUtils';
import CourseCategoryCell from '../../components/CourseCategoryCell';
import CourseLanguageCell from '../../components/CourseLanguageCell';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';

const ASSIGNMENTS_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const ASSIGNMENTS_DEFAULT_FILTERS = {};

/**
 * 과제(Assignment) 관리 페이지 컴포넌트
 * 
 * 과제가 존재 가능한 교육 과정(유료 과정) 목록을 조회하는 페이지입니다.
 */
const AssignmentsPage = () => {
  const navigate = useNavigate();

  const { paginationModel, setPaginationModel } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.assignments,
    ASSIGNMENTS_DEFAULT_PAGINATION,
    ASSIGNMENTS_DEFAULT_FILTERS
  );

  // 백엔드 API 파라미터 구성
  const queryParams = {
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    freeYn: false,
  };

  const { data, isLoading } = useCourseList(queryParams);

  const total = data?.total ?? 0;
  const rowData = data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  /**
   * 행 클릭 핸들러
   */
  const handleRowClick = useCallback((params) => {
    navigate(`/education/assignments/${params.row.courseId}`);
  }, [navigate]);

  // 컬럼 정의 (메모이제이션하여 불필요한 리렌더링 방지)
  const columns = useMemo(() => [
    {
      field: 'courseId',
      headerName: '과정ID',
      flex: 1,
      sortable: false,
      minWidth: 100,
      type: 'number',
    },
    {
      field: 'courseName',
      headerName: '과정명',
      flex: 1,
      sortable: false,
      minWidth: 200,
    },
    {
      field: 'courseCategory',
      headerName: '카테고리',
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => <CourseCategoryCell row={params.row} />,
    },
    {
      field: 'freeYn',
      headerName: '무료 여부',
      flex: 1,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => {
        const label = params.row.freeYn ? '무료' : '유료';
        const color = params.row.freeYn ? 'success' : 'default';
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'languageName',
      headerName: '언어',
      flex: 1,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => {
        return <CourseLanguageCell row={params.row} />;
      },
    },
    {
      field: 'courseRegDate',
      headerName: '등록일',
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        return formatDate(params.row.courseRegDate) || '-';
      },
    },
  ], []);

  return (
    <Container title='과제 관리'>
      <Box sx={{ mb: 2 }}>
        <Button
          component="a"
          href="/source/assessmentGuide.pdf"
          download="과제_가이드.pdf"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
        >
          과제 가이드 다운로드
        </Button>
      </Box>
      <DataGrid
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        height={'auto'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row) => row.courseId}
        onRowClick={handleRowClick}
      />
    </Container>
  );
};

export default AssignmentsPage;
